'use strict';

const libPictProvider = require('pict-provider');

const RNI_HASH = 'Support-Call';
const ACCORDION_HASH = 'SupportMergeWizard';
// The wizard steps, in order. Hashes drive the accordion rail + the body-container ids (#PSA_Body_<ACCORDION_HASH>_<hash>).
const STEPS = [ 'select', 'parent', 'preview', 'confirm' ];
// A reply/forward prefix stripped so a child subject and its parent read as one conversation in the preview.
const REPLY_PREFIX = /^\s*(?:(?:re|fwd?|fw|aw|sv|antw)\s*:\s*)+/i;

/**
 * SupportMergeProvider — the "merge tickets" wizard for the staff console (#/Support). It folds one or more DUPLICATE /
 * misfiled Support-Call tickets into an existing PARENT ticket: the children's messages are re-parented onto the parent
 * (as its comment thread), their attachments move into the parent's observation set, and each child is VOIDED with the
 * reason `Merged into #<parent>` (per Steven's model: Void = "not a real standalone ticket" — spam OR a misfile — while
 * Resolved stays reserved for a real case that was worked). It is the UI twin of the mailbox integration's `reconcile`
 * thread-graph fold. Each wizard merge is itself idempotent (a deterministic `MRG-C-<child>-<parent>` comment GUID so a
 * re-run no-ops), and `reconcile` skips tickets already voided/merged, so the two tools never double-fold the same ticket.
 *
 * Chrome + navigation are owned by `pict-section-accordion` in RenderMode 'wizard' (themed off the same --theme-color-*
 * tokens the console uses); THIS provider renders each step's body into the accordion's body container and performs the
 * merge on the final step's Next (OnComplete). Launches two ways (Support-Provider wires both):
 *   • from an open ticket  → open([thatID])
 *   • bulk from the filtered list → open([id,id,…]); the children come from the filter, but the PARENT is searched
 *     across the WHOLE queue (it need not match the filter — the "15 replies → one ticket" case).
 */
class SupportMergeProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		// The wizard renders INLINE as a console view (Mode 'merge') — NOT a modal. The Support console paints the
		// #SupportMerge-Wizard container (in `_mergeViewHTML`) before calling open(); the accordion builds into it.
		this._mount = '#SupportMerge-Wizard';
		this._accordionBuilt = false;
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get support() { return this.pict.providers.Support; }
	get pickerProvider() { return this.pict.providers['Pict-Section-Picker']; }
	get accordionProvider() { return this.pict.providers['Pict-Section-Accordion']; }
	get permissions() { return this.pict.fable.EffectivePermissionsService; }

	get idUser() { return Number((this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.UserID) || 0); }

	_getJSON(pURL) { return new Promise((resolve, reject) => this.restClient.getJSON(pURL, (pErr, pBody) => (pErr ? reject(pErr) : resolve(pBody)))); }
	_postJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_putJSON(pURL, pBody) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pBody, (pErr, pRes) => (pErr ? reject(pErr) : resolve(pRes)))); }
	_esc(pValue) { return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }
	_toast(pMessage, pType) { const tmpModal = this.pict.views['Pict-Section-Modal']; if (tmpModal && (typeof tmpModal.toast === 'function')) { tmpModal.toast(pMessage, { type: pType || 'info' }); } }

	/** Only a support manager / super-user merges tickets. Delegates to the console's own gate when present. */
	canManage()
	{
		if (this.support && (typeof this.support.canManage === 'function')) { return this.support.canManage(); }
		const tmpPermissions = this.permissions;
		if (!tmpPermissions || (typeof tmpPermissions.canHash !== 'function')) { return true; }
		return tmpPermissions.canHash('Support-Manage');
	}

	get state()
	{
		if (!this.pict.AppData.SupportMerge)
		{
			this.pict.AppData.SupportMerge = { Open: false, Children: [], Parent: null, Preview: null, Busy: false, Error: '', Done: false };
		}
		return this.pict.AppData.SupportMerge;
	}

	_cleanSubject(pValue) { return String(pValue || '').replace(REPLY_PREFIX, '').trim(); }
	_shortDate(pValue)
	{
		const tmpS = String(pValue || ''); if (!tmpS) { return ''; }
		const tmpD = new Date(tmpS); if (Number.isNaN(tmpD.getTime())) { return tmpS.slice(0, 10); }
		return tmpD.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
	}

	// ── Launch / teardown ────────────────────────────────────────────────────────────────────────────────────────

	/**
	 * Build the wizard for one or more child tickets INTO the console's inline merge view. The console (Support-Provider
	 * `enterMerge`) switches to Mode 'merge' and paints the #SupportMerge-Wizard container BEFORE calling this, so the
	 * accordion renders in-flow — there is no overlay/backdrop. Child models are pulled from the console's loaded list
	 * when present (no refetch), else fetched. The target parent is chosen in step 2 and may be any ticket in the queue.
	 * @param {Array<number>} pChildIDs
	 */
	async open(pChildIDs)
	{
		if (!this.canManage()) { this._toast('You do not have permission to merge tickets.', 'error'); return; }
		const tmpIDs = [ ...new Set((Array.isArray(pChildIDs) ? pChildIDs : [ pChildIDs ]).map((pX) => Number(pX)).filter(Boolean)) ];
		if (!tmpIDs.length) { return; }

		const tmpState = this.state;
		tmpState.Open = true; tmpState.Parent = null; tmpState.Preview = null; tmpState.Busy = false; tmpState.Error = ''; tmpState.Done = false;
		tmpState._pickValue = null;   // the picker binds here (ValueAddress); clear so a reused picker doesn't show a stale pick
		tmpState.Children = await this._resolveChildren(tmpIDs);
		// A reused picker view keeps its prior selection across opens — reset it so it agrees with the cleared Parent state.
		const tmpPrevPicker = this.pict.views['SupportMerge-Parent'];
		if (tmpPrevPicker && (typeof tmpPrevPicker.setValue === 'function')) { try { tmpPrevPicker.setValue(''); } catch (pErr) { /* best-effort */ } }

		// The console paints the in-view container synchronously before calling open(); if it's gone (the user navigated
		// away during the async child resolve above), bail quietly rather than building into nothing.
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpCA || !tmpCA.getElement(this._mount).length) { return; }
		this._buildWizard();
	}

	/** Reset wizard state when the console leaves the merge view (cancel / navigate-away / after a merge). The accordion
	 *  view object is reused across opens (createAccordion reconfigures + resets it), so there's nothing to destroy —
	 *  just drop the transient state and clear the reused parent picker so the next open starts clean. */
	teardown()
	{
		const tmpState = this.state;
		tmpState.Open = false; tmpState.Parent = null; tmpState.Preview = null; tmpState.Busy = false; tmpState.Done = false; tmpState._pickValue = null;
		const tmpPicker = this.pict.views['SupportMerge-Parent'];
		if (tmpPicker && (typeof tmpPicker.setValue === 'function')) { try { tmpPicker.setValue(''); } catch (pErr) { /* best-effort */ } }
	}

	/** Build child row models from the console's loaded calls when available, else fetch each Document. */
	async _resolveChildren(pIDs)
	{
		const tmpLoaded = (this.support && this.support.state && Array.isArray(this.support.state.Calls)) ? this.support.state.Calls : [];
		const tmpByID = {}; tmpLoaded.forEach((pC) => { tmpByID[Number(pC.IDDocument)] = pC; });
		const tmpOut = [];
		for (const pID of pIDs)
		{
			if (tmpByID[pID]) { tmpOut.push({ IDDocument: pID, Summary: tmpByID[pID].Summary, Caller: tmpByID[pID].Caller, Status: tmpByID[pID].Status, Designated: tmpByID[pID].Designated }); continue; }
			try
			{
				const tmpDoc = await this._getJSON(`Document/${pID}`);
				let tmpFD = tmpDoc && tmpDoc.FormData; if (typeof tmpFD === 'string') { try { tmpFD = JSON.parse(tmpFD); } catch (pErr) { tmpFD = {}; } }
				const tmpIssue = (tmpFD && tmpFD.Issue) || {};
				tmpOut.push({ IDDocument: pID, Summary: tmpIssue.Summary || (tmpDoc && tmpDoc.Name) || `#${pID}`, Caller: (tmpFD && tmpFD.Caller && tmpFD.Caller.Name) || '', Status: (tmpDoc && tmpDoc.Status) || '', Designated: (tmpDoc && tmpDoc.DocumentDate) || '' });
			}
			catch (pError) { tmpOut.push({ IDDocument: pID, Summary: `#${pID}`, Caller: '', Status: '', Designated: '' }); }
		}
		return tmpOut;
	}

	// ── Wizard shell ─────────────────────────────────────────────────────────────────────────────────────────────

	_buildWizard()
	{
		const tmpAccordion = this.accordionProvider;
		if (!tmpAccordion || (typeof tmpAccordion.createAccordion !== 'function')) { this._toast('The wizard component is unavailable.', 'error'); return; }
		tmpAccordion.createAccordion(ACCORDION_HASH,
			{
				DestinationAddress: '#SupportMerge-Wizard',
				RenderMode: 'wizard',
				DoneLabel: 'Merge tickets',
				Steps:
				[
					{ Hash: 'select',  Title: 'Select',      Subtitle: this.state.Children.length === 1 ? '1 ticket' : `${this.state.Children.length} tickets` },
					{ Hash: 'parent',  Title: 'Find parent', Subtitle: 'The ticket to merge into', CanAdvance: () => (this.state.Parent ? true : { allow: false, message: 'Pick a ticket to merge into.' }) },
					{ Hash: 'preview', Title: 'Preview',     Subtitle: 'What the merge does' },
					{ Hash: 'confirm', Title: 'Confirm',     Subtitle: 'Apply the merge' },
				],
				// pict-section-accordion fires OnStepChange with the step OBJECT (not the hash string) — read .Hash.
				OnStepChange: (pTo) => this._renderStepBody(pTo && (pTo.Hash || pTo)),
				OnComplete: () => this.performMerge(),
			});
		const tmpView = this.pict.views[ACCORDION_HASH];
		if (tmpView && (typeof tmpView.render === 'function')) { tmpView.render(); }
		this._accordionBuilt = true;
		// Render every body up front (cheap), then the accordion toggles visibility; lazy bodies (preview) refresh on entry.
		STEPS.forEach((pHash) => this._renderStepBody(pHash));
	}

	_bodyMount(pHash) { return `#PSA_Body_${ACCORDION_HASH}_${pHash}`; }
	_assignBody(pHash, pHTML) { const tmpCA = this.pict.ContentAssignment; if (tmpCA && tmpCA.getElement(this._bodyMount(pHash)).length) { tmpCA.assignContent(this._bodyMount(pHash), pHTML); } }

	_renderStepBody(pHash)
	{
		switch (pHash)
		{
			case 'select': this._assignBody('select', this._selectHTML()); break;
			case 'parent': this._assignBody('parent', this._parentHTML()); this._mountParentPicker(); break;
			case 'preview': this._assignBody('preview', this._previewHTML()); this._loadPreviewAsync(); break;
			case 'confirm': this._assignBody('confirm', this._confirmHTML()); break;
			default: break;
		}
	}

	// ── Step 1 — the children ────────────────────────────────────────────────────────────────────────────────────

	_childCardHTML(pChild)
	{
		return `<div class="supmg-tk">`
			+ `<div class="supmg-tk-top"><span class="sup-badge sup-st-open"><i class="fas fa-inbox"></i> ${this._esc(pChild.Status || 'Open')}</span><span class="supmg-tk-id">#${pChild.IDDocument}</span></div>`
			+ `<div class="supmg-tk-sum">${this._esc(pChild.Summary || '(no summary)')}</div>`
			+ `<div class="supmg-tk-meta">${pChild.Caller ? `<i class="fas fa-user"></i> ${this._esc(pChild.Caller)} · ` : ''}<i class="fas fa-clock"></i> ${this._esc(this._shortDate(pChild.Designated))}</div>`
			+ `</div>`;
	}

	_selectHTML()
	{
		const tmpN = this.state.Children.length;
		return `<div class="supmg-lede">You're merging ${tmpN === 1 ? 'this ticket' : `these ${tmpN} tickets`} into another:</div>`
			+ `<div class="supmg-tklist">${this.state.Children.map((pC) => this._childCardHTML(pC)).join('')}</div>`
			+ `<div class="supmg-note"><i class="fas fa-circle-info"></i> ${tmpN === 1 ? 'Its' : 'Their'} messages and attachments move into the ticket you pick next. ${tmpN === 1 ? 'It is' : 'They are'} then voided with the reason “Merged into #…” and leave the open queue.</div>`;
	}

	// ── Step 2 — find the parent (searchable picker over the whole queue) ─────────────────────────────────────────

	_parentHTML()
	{
		return `<div class="supmg-lede">Search for the ticket to merge into:</div>`
			+ `<div id="SupportMerge-ParentPick" class="supmg-pick"></div>`
			+ `<div id="SupportMerge-ParentSel" class="supmg-parentsel">${this._parentSelHTML()}</div>`
			+ `<div class="supmg-note"><i class="fas fa-ban"></i> The ticket${this.state.Children.length === 1 ? '' : 's'} you're merging ${this.state.Children.length === 1 ? 'is' : 'are'} excluded automatically, and already-voided tickets can't be a target.</div>`;
	}

	_parentSelHTML()
	{
		const tmpP = this.state.Parent;
		if (!tmpP) { return `<div class="supmg-parentempty">No target chosen yet.</div>`; }
		return `<div class="supmg-tk supmg-tk-sel"><div class="supmg-tk-top"><span class="supmg-pill-into">Merge into</span><span class="supmg-tk-id">#${tmpP.IDDocument}</span></div>`
			+ `<div class="supmg-tk-sum">${this._esc(tmpP.Summary || '(no summary)')}</div>`
			+ `<div class="supmg-tk-meta">${tmpP.Caller ? `<i class="fas fa-user"></i> ${this._esc(tmpP.Caller)} · ` : ''}<i class="fas fa-clock"></i> ${this._esc(this._shortDate(tmpP.Designated))}</div></div>`;
	}

	/** Candidate target tickets: the console's loaded queue, minus the children and any already-Void ticket. */
	_parentCandidates()
	{
		const tmpChildIDs = {}; this.state.Children.forEach((pC) => { tmpChildIDs[Number(pC.IDDocument)] = true; });
		const tmpCalls = (this.support && this.support.state && Array.isArray(this.support.state.Calls)) ? this.support.state.Calls : [];
		return tmpCalls.filter((pC) => !tmpChildIDs[Number(pC.IDDocument)] && String(pC.Status) !== 'Void');
	}

	_mountParentPicker()
	{
		const tmpPicker = this.pickerProvider;
		const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA || !tmpCA.getElement('#SupportMerge-ParentPick').length) { return; }
		const tmpInstance = tmpPicker.createPicker('SupportMerge-Parent',
			{
				DestinationAddress: '#SupportMerge-ParentPick',
				ValueAddress: 'AppData.SupportMerge._pickValue',
				Searchable: true,
				AllowClear: true,
				ClearLabel: '— none —',
				Placeholder: 'Search tickets by subject, #, or caller…',
				// pict-section-picker contract: DataProvider(term, page) → Promise<{results:[{Value,Text}], hasMore}>.
				// A pure #id term does an EXACT SERVER lookup (so a parent of ANY age — older than the console's recent,
				// capped window — is still findable, the "15 replies → one old ticket" case); a text term filters the
				// loaded recent queue client-side, PAGED (honoring the page arg so "Load more" terminates, not re-appends).
				DataProvider: (pTerm, pPage) =>
				{
					const tmpQ = String(pTerm || '').trim();
					const tmpPage = Number(pPage) || 0;
					const tmpChildIDs = {}; this.state.Children.forEach((pC) => { tmpChildIDs[Number(pC.IDDocument)] = true; });
					if (/^#?\d+$/.test(tmpQ))
					{
						const tmpID = Number(tmpQ.replace('#', ''));
						if (tmpChildIDs[tmpID]) { return Promise.resolve({ results: [], hasMore: false }); }
						return this._lookupSupportDoc(tmpID)
							.then((pDoc) => (!pDoc || String(pDoc.Status) === 'Void') ? { results: [], hasMore: false } : { results: [ this._candidateOption(pDoc) ], hasMore: false })
							.catch(() => ({ results: [], hasMore: false }));
					}
					const tmpLc = tmpQ.toLowerCase();
					let tmpRows = this._parentCandidates();
					if (tmpLc) { tmpRows = tmpRows.filter((pC) => String(pC.Summary || '').toLowerCase().indexOf(tmpLc) >= 0 || String(pC.Caller || '').toLowerCase().indexOf(tmpLc) >= 0); }
					const tmpSize = 50;
					const tmpSlice = tmpRows.slice(tmpPage * tmpSize, (tmpPage + 1) * tmpSize);
					return Promise.resolve({ results: tmpSlice.map((pC) => this._candidateOption(pC)), hasMore: tmpRows.length > (tmpPage + 1) * tmpSize });
				},
				ResolveValue: (pValue) =>
				{
					const tmpRow = this._parentCandidates().find((pC) => Number(pC.IDDocument) === Number(pValue));
					if (tmpRow) { return Promise.resolve(this._candidateOption(tmpRow)); }
					return this._lookupSupportDoc(Number(pValue)).then((pDoc) => (pDoc ? this._candidateOption(pDoc) : null)).catch(() => null);
				},
				OnChange: (pValue) => this._onParentChosen(Number(pValue) || 0),
			});
		if (tmpInstance && (typeof tmpInstance.render === 'function')) { tmpInstance.render(); }
	}

	/** A picker option {Value,Text} for a candidate/doc row. */
	_candidateOption(pC) { return { Value: Number(pC.IDDocument), Text: `#${pC.IDDocument} · ${this._cleanSubject(pC.Summary) || '(no summary)'}${pC.Caller ? ` — ${pC.Caller}` : ''}` }; }

	/** Server-fetch a single Support-Call Document → the candidate row shape, or null. Finds parents of any age. */
	async _lookupSupportDoc(pID)
	{
		if (!pID) { return null; }
		const tmpRows = await this._getJSON(`Documents/FilteredTo/FBV~DocumentType~EQ~${RNI_HASH}~FBV~IDDocument~EQ~${Number(pID)}~FBV~Deleted~EQ~0/0/1`).catch(() => null);
		const tmpDoc = (Array.isArray(tmpRows) && tmpRows[0]) ? tmpRows[0] : null;
		if (!tmpDoc) { return null; }
		let tmpFD = tmpDoc.FormData; if (typeof tmpFD === 'string') { try { tmpFD = JSON.parse(tmpFD); } catch (pErr) { tmpFD = {}; } }
		const tmpIssue = (tmpFD && tmpFD.Issue) || {}; const tmpMeta = (tmpFD && tmpFD.Meta) || {};
		return { IDDocument: Number(tmpDoc.IDDocument), Summary: tmpIssue.Summary || tmpDoc.Name || `#${tmpDoc.IDDocument}`, Caller: (tmpFD && tmpFD.Caller && tmpFD.Caller.Name) || '', Designated: tmpMeta.ReceivedAt || tmpMeta.FirstEmail || tmpDoc.DocumentDate || tmpDoc.CreateDate || '', Status: tmpDoc.Status || '' };
	}

	_repaintParentSel()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA && tmpCA.getElement('#SupportMerge-ParentSel').length) { tmpCA.assignContent('#SupportMerge-ParentSel', this._parentSelHTML()); }
	}

	_onParentChosen(pIDDocument)
	{
		this.state.Preview = null;   // a new parent invalidates the loaded preview
		if (!pIDDocument) { this.state.Parent = null; this._repaintParentSel(); return; }
		const tmpRow = this._parentCandidates().find((pC) => Number(pC.IDDocument) === pIDDocument);
		if (tmpRow) { this.state.Parent = tmpRow; this._repaintParentSel(); return; }
		// Chosen by #id and not in the loaded set — show it immediately, then resolve its real subject from the server.
		this.state.Parent = { IDDocument: pIDDocument, Summary: `#${pIDDocument}`, Caller: '', Designated: '' };
		this._repaintParentSel();
		this._lookupSupportDoc(pIDDocument).then((pDoc) =>
		{
			if (pDoc && this.state.Parent && Number(this.state.Parent.IDDocument) === pIDDocument) { this.state.Parent = pDoc; this.state.Preview = null; this._repaintParentSel(); }
		}).catch(() => { /* keep the #id placeholder */ });
	}

	// ── Step 3 — preview the merged thread ───────────────────────────────────────────────────────────────────────

	_previewHTML()
	{
		const tmpP = this.state.Parent;
		if (!tmpP) { return `<div class="supmg-note"><i class="fas fa-triangle-exclamation"></i> Pick a target ticket first.</div>`; }
		const tmpPrev = this.state.Preview;
		const tmpHeader = `<div class="supmg-prev-head"><span class="supmg-muted">Into</span> <b>#${tmpP.IDDocument}</b> · ${this._esc(this._cleanSubject(tmpP.Summary) || '(no summary)')}</div>`;
		if (!tmpPrev) { return tmpHeader + `<div class="supmg-note"><i class="fas fa-spinner fa-spin"></i> Loading the thread…</div>`; }
		if (tmpPrev.error) { return tmpHeader + `<div class="supmg-err"><i class="fas fa-triangle-exclamation"></i> Couldn't load the thread preview: ${this._esc(tmpPrev.error)}. The merge will still work.</div>`; }
		const tmpNodes = tmpPrev.nodes.map((pNode) =>
			`<div class="supmg-node ${pNode.added ? 'supmg-node-add' : ''}">`
			+ `<div class="supmg-node-h">${pNode.added ? '<i class="fas fa-plus"></i> ' : ''}${this._esc(pNode.title)}</div>`
			+ `<div class="supmg-node-m">${this._esc(pNode.meta)}${pNode.added ? ` · from #${pNode.fromID}` : ''}</div></div>`).join('');
		const tmpAtt = tmpPrev.attachments > 0
			? `<div class="supmg-note"><i class="fas fa-photo-film"></i> ${tmpPrev.attachments} attachment${tmpPrev.attachments === 1 ? '' : 's'} from the merged ticket${this.state.Children.length === 1 ? '' : 's'} will join this ticket's observation set.</div>`
			: '';
		return tmpHeader + `<div class="supmg-tl">${tmpNodes}</div>` + tmpAtt;
	}

	async _loadPreviewAsync()
	{
		const tmpP = this.state.Parent;
		if (!tmpP || this.state.Preview) { return; }
		try
		{
			// Parent thread = its comment stream; each child contributes its summary node (+ its own replies) spliced in by date.
			const tmpParentComments = await this._getJSON(`DocumentComments/FilteredTo/FBV~IDDocument~EQ~${tmpP.IDDocument}~FBV~Deleted~EQ~0/0/200`).catch(() => []);
			const tmpNodes = [];
			tmpNodes.push({ title: this._cleanSubject(tmpP.Summary) || `#${tmpP.IDDocument}`, meta: `${this._shortDate(tmpP.Designated)} · the parent ticket`, added: false });
			(Array.isArray(tmpParentComments) ? tmpParentComments : []).forEach((pC) =>
			{
				let tmpMeta = {}; try { tmpMeta = pC.Metadata ? JSON.parse(pC.Metadata) : {}; } catch (pErr) { tmpMeta = {}; }
				tmpNodes.push({ title: tmpMeta.Kind === 'InboundEmail' ? 'Reply' : (tmpMeta.Kind || 'Note'), meta: `${this._shortDate(pC.CreateDate)}`, added: false });
			});
			let tmpAttachments = 0;
			for (const pChild of this.state.Children)
			{
				tmpNodes.push({ title: this._cleanSubject(pChild.Summary) || `#${pChild.IDDocument}`, meta: this._shortDate(pChild.Designated), added: true, fromID: pChild.IDDocument });
				try { const tmpObs = await this._childObservations(pChild.IDDocument); tmpAttachments += tmpObs.length; } catch (pErr) { /* count best-effort */ }
			}
			if (this.state.Parent !== tmpP) { return; }   // parent changed mid-load → a newer load owns the preview
			this.state.Preview = { nodes: tmpNodes, attachments: tmpAttachments };
		}
		catch (pError)
		{
			if (this.state.Parent !== tmpP) { return; }
			this.state.Preview = { nodes: [], attachments: 0, error: (pError && pError.message) || String(pError) };
		}
		if (this.state.Parent !== tmpP) { return; }
		this._assignBody('preview', this._previewHTML());
	}

	// ── Step 4 — confirm ─────────────────────────────────────────────────────────────────────────────────────────

	_confirmHTML()
	{
		const tmpP = this.state.Parent; const tmpN = this.state.Children.length;
		if (!tmpP) { return `<div class="supmg-note"><i class="fas fa-triangle-exclamation"></i> Pick a target ticket first.</div>`; }
		if (this.state.Done)
		{
			return `<div class="supmg-done"><div class="supmg-done-ic"><i class="fas fa-check"></i></div>`
				+ `<div class="supmg-done-t">Merged into #${tmpP.IDDocument}</div>`
				+ `<div class="supmg-done-s">${tmpN} ticket${tmpN === 1 ? '' : 's'} voided as “Merged into #${tmpP.IDDocument}” and left the open queue.</div></div>`;
		}
		const tmpArrow = `<div class="supmg-arrow">${this.state.Children.map((pC) => `<span class="sup-badge sup-st-open">#${pC.IDDocument}</span>`).join('')}<i class="fas fa-arrow-right"></i><span class="sup-badge sup-st-open">#${tmpP.IDDocument}</span></div>`;
		const tmpList = `<div class="supmg-outcome">`
			+ `<div><i class="fas fa-check"></i> ${tmpN} message thread${tmpN === 1 ? '' : 's'} appended to #${tmpP.IDDocument}</div>`
			+ `<div><i class="fas fa-check"></i> attachments moved into its observation set</div>`
			+ `<div><i class="fas fa-check"></i> ${tmpN === 1 ? 'the ticket' : `the ${tmpN} tickets`} marked <b>Void · Merged into #${tmpP.IDDocument}</b>, removed from the open queue</div></div>`;
		const tmpNote = `<div class="supmg-note"><i class="fas fa-rotate-left"></i> Nothing is deleted — a merged ticket stays reopenable (its moved attachments stay with the parent).</div>`;
		const tmpErr = this.state.Error ? `<div class="supmg-err"><i class="fas fa-triangle-exclamation"></i> ${this._esc(this.state.Error)}</div>` : '';
		const tmpBusy = this.state.Busy ? `<div class="supmg-note"><i class="fas fa-spinner fa-spin"></i> Merging…</div>` : '';
		return tmpArrow + tmpList + tmpNote + tmpErr + tmpBusy;
	}

	// ── The merge operation ──────────────────────────────────────────────────────────────────────────────────────

	/**
	 * The observation ids attached to a child Document, as [{IDObservation}]. Attachments link through
	 * DocumentObservationJoin (there is NO Observation.IDDocument column), so read the join rows; fall back to the
	 * parsed FormData.ObservationImageSet when the join query yields nothing.
	 */
	async _childObservations(pIDDocument)
	{
		const tmpOut = []; const tmpSeen = {};
		const tmpAdd = (pID) => { const tmpN = Number(pID); if (tmpN && !tmpSeen[tmpN]) { tmpSeen[tmpN] = true; tmpOut.push({ IDObservation: tmpN }); } };
		const tmpJoins = await this._getJSON(`DocumentObservationJoins/FilteredTo/FBV~IDDocument~EQ~${pIDDocument}~FBV~Deleted~EQ~0/0/200`).catch(() => []);
		(Array.isArray(tmpJoins) ? tmpJoins : []).forEach((pJ) => tmpAdd(pJ && pJ.IDObservation));
		if (!tmpOut.length)
		{
			try
			{
				const tmpDoc = await this._getJSON(`Document/${pIDDocument}`);
				let tmpFD = tmpDoc && tmpDoc.FormData; if (typeof tmpFD === 'string') { try { tmpFD = JSON.parse(tmpFD); } catch (pErr) { tmpFD = {}; } }
				const tmpSet = (tmpFD && Array.isArray(tmpFD.ObservationImageSet)) ? tmpFD.ObservationImageSet : [];
				tmpSet.forEach((pItem) => tmpAdd(pItem && (pItem.IDObservation || pItem)));
			}
			catch (pErr) { /* best-effort */ }
		}
		return tmpOut;
	}

	async performMerge()
	{
		const tmpState = this.state;
		const tmpP = tmpState.Parent;
		if (!tmpP || tmpState.Busy || tmpState.Done) { return; }
		tmpState.Busy = true; tmpState.Error = '';
		this._assignBody('confirm', this._confirmHTML());
		try
		{
			for (const pChild of tmpState.Children) { await this._mergeOne(pChild.IDDocument, tmpP.IDDocument); }
			tmpState.Busy = false; tmpState.Done = true;
			this._assignBody('confirm', this._confirmHTML());
			this._toast(`Merged ${tmpState.Children.length} ticket${tmpState.Children.length === 1 ? '' : 's'} into #${tmpP.IDDocument}.`, 'success');
		}
		catch (pError)
		{
			tmpState.Busy = false;
			tmpState.Error = (pError && pError.message) ? pError.message : String(pError);
			this._assignBody('confirm', this._confirmHTML());
			this._toast(`Merge failed: ${tmpState.Error}`, 'error');
		}
		finally
		{
			// Clear the list selection in BOTH cases — a partial failure still voided some children (a retry is idempotent
			// via the MRG-C key, so re-merging the rest is safe). The merge view stays put showing the Done/error card; the
			// list is reloaded when the user leaves via the view's "All calls" back link (Support.exitMerge → loadAsync),
			// so the just-merged tickets drop out of the queue then — no reload here (it would blow away the Done card).
			if (this.support && (typeof this.support.clearSelection === 'function')) { this.support.clearSelection(); }
		}
	}

	/**
	 * Fold one child ticket into the parent: re-parent its comment thread, move its observations, stamp Meta.MergedInto,
	 * and VOID it with the reason `Merged into #<parent>`. Idempotent — a merge comment carries a deterministic
	 * GUIDComment (MRG-C-<child>) so a re-run no-ops, and the child is left Deleted=0 so a reopen restores it.
	 */
	async _mergeOne(pChildID, pParentID)
	{
		if (Number(pChildID) === Number(pParentID)) { return; }

		const tmpChildDoc = await this._getJSON(`Document/${pChildID}`);
		let tmpChildFD = tmpChildDoc && tmpChildDoc.FormData; if (typeof tmpChildFD === 'string') { try { tmpChildFD = JSON.parse(tmpChildFD); } catch (pErr) { tmpChildFD = {}; } }
		tmpChildFD = tmpChildFD || {};
		const tmpIssue = tmpChildFD.Issue || {};
		// Idempotency key is (child, parent) so a reopened child re-merged to a DIFFERENT parent still carries its original
		// report to the new parent. 'MRG-C-<child>-<parent>' stays ≤36 (the GUIDComment column size).
		const tmpGuid = `MRG-C-${pChildID}-${pParentID}`;

		// 1. Append the child's original report onto the parent as a comment (once). The parent JOIN lives OUTSIDE the
		//    existence guard (idempotent, dup-safe) so a retry after a mid-way failure still links an already-created comment.
		const tmpExisting = await this._getJSON(`Comments/FilteredTo/FBV~GUIDComment~EQ~${tmpGuid}/0/1`).catch(() => []);
		let tmpIDComment = (Array.isArray(tmpExisting) && tmpExisting[0]) ? Number(tmpExisting[0].IDComment) : 0;
		if (!tmpIDComment)
		{
			const tmpBody = String(tmpIssue.Details || tmpIssue.Summary || tmpChildDoc.Name || '(no content)').trim() || '(no content)';
			const tmpMeta = { Kind: 'InboundEmail', MergedFromIDDocument: Number(pChildID), MergedBy: this.idUser, OriginalSubject: tmpIssue.Summary || tmpChildDoc.Name || '', OriginalDate: tmpChildDoc.DocumentDate || '' };
			const tmpComment = await this._postJSON('Comment', { Comment: tmpBody, CommentHtml: this._esc(tmpBody), GUIDComment: tmpGuid, Metadata: JSON.stringify(tmpMeta) });
			tmpIDComment = tmpComment && Number(tmpComment.IDComment);
		}
		if (tmpIDComment) { try { await this._postJSON('DocumentCommentJoins', [ { IDDocument: Number(pParentID), IDComment: tmpIDComment } ]); } catch (pErr) { /* join may already exist */ } }

		// 2. Re-parent the child's own reply comments onto the parent (add a join; a dup join is harmless/deduped).
		const tmpChildComments = await this._getJSON(`DocumentComments/FilteredTo/FBV~IDDocument~EQ~${pChildID}~FBV~Deleted~EQ~0/0/200`).catch(() => []);
		for (const pC of (Array.isArray(tmpChildComments) ? tmpChildComments : []))
		{
			if (!pC || !pC.IDComment) { continue; }
			try { await this._postJSON('DocumentCommentJoins', [ { IDDocument: Number(pParentID), IDComment: Number(pC.IDComment) } ]); } catch (pErr) { /* join may already exist */ }
		}

		// 3. Move the child's attachments into the parent's observation set via the canonical join endpoints
		//    (Document/:ID/AddObservation/:ID + .../RemoveObservation/:ID — there is no Observation.IDDocument column).
		const tmpObs = await this._childObservations(pChildID);
		const tmpMovedIDs = [];
		for (const pO of tmpObs)
		{
			const tmpIDObs = Number(pO && pO.IDObservation);
			if (!tmpIDObs) { continue; }
			try
			{
				await this._getJSON(`Document/${Number(pParentID)}/AddObservation/${tmpIDObs}`);
				await this._getJSON(`Document/${Number(pChildID)}/RemoveObservation/${tmpIDObs}`);
				tmpMovedIDs.push(tmpIDObs);
			}
			catch (pErr) { /* best-effort per observation */ }
		}
		if (tmpMovedIDs.length) { await this._mergeObservationSet(Number(pParentID), tmpMovedIDs); }

		// 4. Stamp the child's Meta and VOID it (reason carries the parent). Left Deleted=0 → reopen restores it.
		tmpChildFD.Meta = Object.assign({}, tmpChildFD.Meta, { MergedInto: Number(pParentID), MergedAt: (new Date()).toISOString(), MergedBy: this.idUser, MergedVia: 'ui-merge-wizard' });
		const tmpVoid = await this._putJSON('Document', { IDDocument: Number(pChildID), Status: 'Void', StateName: `Merged into #${pParentID}`, _StatusOverride: true, FormData: tmpChildFD });
		if (tmpVoid && (tmpVoid.Error || tmpVoid.ErrorCode)) { throw new Error(`#${pChildID}: ${String(tmpVoid.Error || tmpVoid.ErrorCode)}`); }
	}

	/** Add moved observation ids to the parent's FormData.ObservationImageSet so its attachment control shows them. */
	async _mergeObservationSet(pParentID, pObservationIDs)
	{
		try
		{
			const tmpDoc = await this._getJSON(`Document/${pParentID}`);
			let tmpFD = tmpDoc && tmpDoc.FormData; if (typeof tmpFD === 'string') { try { tmpFD = JSON.parse(tmpFD); } catch (pErr) { tmpFD = {}; } }
			tmpFD = tmpFD || {};
			const tmpSet = Array.isArray(tmpFD.ObservationImageSet) ? tmpFD.ObservationImageSet.slice() : [];
			const tmpSeen = {}; tmpSet.forEach((pItem) => { tmpSeen[Number(pItem && (pItem.IDObservation || pItem))] = true; });
			pObservationIDs.forEach((pID) => { if (!tmpSeen[Number(pID)]) { tmpSet.push({ IDObservation: Number(pID) }); } });
			tmpFD.ObservationImageSet = tmpSet;
			await this._putJSON('Document', { IDDocument: Number(pParentID), FormData: tmpFD });
		}
		catch (pError) { /* the observations are already re-parented; the set hint is best-effort */ }
	}
}

module.exports = SupportMergeProvider;
module.exports.default_configuration = { ProviderIdentifier: 'SupportMerge' };
